/**
 * Canonical display names for the assumable role keys. Single source of truth
 * for the role-switch UI (RoleSwitcher, AssumedRoleBanner) and audit/activity
 * actor rendering — do not re-declare this map elsewhere.
 */
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  "role:facility:manager": "Facility Manager",
  "role:staff:medical": "Medical Staff",
  "role:staff:care": "Care Staff",
};

/** Human label for a role key, falling back to the raw key if unmapped. */
export function roleDisplayName(roleKey: string): string {
  return ROLE_DISPLAY_NAMES[roleKey] ?? roleKey;
}

/**
 * SCRUM-146: suffix marking that an action was taken under an assumed role
 * (a testing/training role-switch — never enabled in production). Appended to
 * the actor in audit/activity feeds so the trail reflects the role in effect,
 * not just the actor's stored name/primary role. Empty when no role was assumed.
 */
export function assumedRoleSuffix(assumedRoleName?: string | null): string {
  return assumedRoleName
    ? ` (acting as ${roleDisplayName(assumedRoleName)})`
    : "";
}

/**
 * Format an audit/activity actor: their name (or email, or "System") plus the
 * assumed-role suffix when present.
 */
export function formatAuditActor(actor: {
  user_name?: string | null;
  user_email?: string | null;
  assumed_role_name?: string | null;
}): string {
  const base = actor.user_name || actor.user_email || "System";
  return `${base}${assumedRoleSuffix(actor.assumed_role_name)}`;
}
